/**
 * World-time arithmetic of the chronicle (the cursor bar, the date picker, Tia's advance_time): one formula for the
 * label, the sortKey and the day rollover, so the cursor the GM moves and the one Tia moves are the same value.
 */
import type { InWorldTime, CampaignCalendar } from './domain.js';
import type { TimeOfDay } from './utils.js';

/** The 8 parts of day, in order. */
export const TIME_OF_DAY_CYCLE: TimeOfDay[] = [
  'dawn', 'morning', 'midday', 'afternoon', 'dusk', 'evening', 'night', 'midnight',
];

/** Parts drawn as landmarks under the bar. */
export const TIME_OF_DAY_LANDMARKS: TimeOfDay[] = ['dawn', 'midday', 'dusk', 'midnight'];

const DEFAULT_MONTH_DAYS = 30;

export const currentPart = (cursor: InWorldTime): TimeOfDay => cursor.timeOfDay ?? 'morning';

const hasMonths = (calendar: CampaignCalendar | undefined): calendar is CampaignCalendar =>
  !!calendar && calendar.months.length > 0;

/** Days in a month of the calendar; null without a calendar (days are not bounded). */
export function monthDays(calendar: CampaignCalendar | undefined, month: number): number | null {
  if (!hasMonths(calendar)) return null;
  return calendar.months[month]?.days ?? DEFAULT_MONTH_DAYS;
}

/** «2 Mirtul, 1491 DR» / «Day 2, 1491»; null when the cursor has no day. */
export function formatWorldDate(cursor: InWorldTime, calendar: CampaignCalendar | undefined, dayWord: string): string | null {
  if (cursor.day == null) return null;
  const monthName = calendar?.months[cursor.month ?? 0]?.name;
  const yearStr = cursor.year != null && calendar?.yearLabel
    ? `, ${cursor.year} ${calendar.yearLabel}`
    : cursor.year != null ? `, ${cursor.year}` : '';
  if (monthName) return `${cursor.day} ${monthName}${yearStr}`;
  return `${dayWord} ${cursor.day}${yearStr}`;
}


/**
 * Label and sortKey follow the structured date (the same formula as the event
 * form), so the cursor sorts with events. Without a day both stay as they are.
 */
export function withDerived(cursor: InWorldTime, calendar: CampaignCalendar | undefined, dayWord: string): InWorldTime {
  if (cursor.day == null) return cursor;
  let dayOfYear = 0;
  if (hasMonths(calendar)) {
    for (let i = 0; i < (cursor.month ?? 0); i++) dayOfYear += calendar.months[i]?.days ?? 0;
  }
  dayOfYear += cursor.day;
  const sortKey = (cursor.year ?? 0) * 10000 + dayOfYear * 10 + TIME_OF_DAY_CYCLE.indexOf(currentPart(cursor));
  return { ...cursor, sortKey, label: formatWorldDate(cursor, calendar, dayWord) ?? cursor.label };
}

/** Move the date by `delta` days, wrapping months and years by the calendar. */
function moveDays(cursor: InWorldTime, delta: number, calendar: CampaignCalendar | undefined): InWorldTime {
  let day = (cursor.day ?? 1) + delta;
  let month = cursor.month ?? 0;
  let year = cursor.year;
  if (hasMonths(calendar)) {
    const len = (m: number) => calendar.months[m]?.days ?? DEFAULT_MONTH_DAYS;
    while (day > len(month)) {
      day -= len(month);
      month++;
      if (month >= calendar.months.length) {
        month = 0;
        if (year != null) year++;
      }
    }
    while (day < 1) {
      month--;
      if (month < 0) {
        month = calendar.months.length - 1;
        if (year != null) year--;
      }
      day += len(month);
    }
  } else {
    day = Math.max(1, day);
  }
  return { ...cursor, day, month, year };
}

/** One day forward / back; the part of day stays. */
export function shiftDay(cursor: InWorldTime, delta: number, calendar: CampaignCalendar | undefined, dayWord: string): InWorldTime {
  return withDerived(moveDays(cursor, delta, calendar), calendar, dayWord);
}

/**
 * The part `tod`. A part earlier than the current one is the next day (the
 * world does not go back by a click on the bar); the same part is no change.
 */
export function jumpToPart(cursor: InWorldTime, tod: TimeOfDay, calendar: CampaignCalendar | undefined, dayWord: string): InWorldTime {
  const from = TIME_OF_DAY_CYCLE.indexOf(currentPart(cursor));
  const to = TIME_OF_DAY_CYCLE.indexOf(tod);
  const next: InWorldTime = { ...cursor, timeOfDay: tod };
  const moved = to < from && cursor.day != null ? moveDays(next, 1, calendar) : next;
  return withDerived(moved, calendar, dayWord);
}

/** One part forward / back (◂ ▸, Alt+← / Alt+→): past midnight is the next dawn and back. */
export function stepPart(cursor: InWorldTime, delta: 1 | -1, calendar: CampaignCalendar | undefined, dayWord: string): InWorldTime {
  const n = TIME_OF_DAY_CYCLE.length;
  const raw = TIME_OF_DAY_CYCLE.indexOf(currentPart(cursor)) + delta;
  const next: InWorldTime = { ...cursor, timeOfDay: TIME_OF_DAY_CYCLE[(raw + n) % n] };
  const crossed = raw >= n ? 1 : raw < 0 ? -1 : 0;
  const moved = crossed !== 0 && cursor.day != null ? moveDays(next, crossed, calendar) : next;
  return withDerived(moved, calendar, dayWord);
}

/** Set any of day / month / year; the day is kept inside the month. */
export function setDate(
  cursor: InWorldTime,
  change: { day?: number; month?: number; year?: number },
  calendar: CampaignCalendar | undefined,
  dayWord: string,
): InWorldTime {
  const month = change.month ?? cursor.month ?? 0;
  const max = monthDays(calendar, month);
  const wanted = Math.max(1, change.day ?? cursor.day ?? 1);
  const next: InWorldTime = {
    ...cursor,
    day: max != null ? Math.min(wanted, max) : wanted,
    month,
    year: change.year ?? cursor.year,
  };
  return withDerived(next, calendar, dayWord);
}

/** Same day, month, year and part of day. */
export function sameMoment(a: InWorldTime, b: InWorldTime): boolean {
  return a.day === b.day && a.month === b.month && a.year === b.year && a.timeOfDay === b.timeOfDay
    && a.label === b.label;
}
